/**
 * Imperative confirm/prompt dialogs — replaces native window.confirm / window.prompt
 * with the custom ConfirmDialog, callable as `await confirm({...})` / `await prompt({...})`.
 *
 * Usage:
 *   const { confirm, prompt } = useDialogs();
 *   if (!(await confirm({ title: 'Delete site?', description: '…', variant: 'danger', confirmText: 'Delete' }))) return;
 *   const name = await prompt({ title: 'Rename', defaultValue: cur });  // null if cancelled
 *
 * Mount <DialogsProvider> once near the app root.
 */
import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert01Icon as AlertTriangle, Delete01Icon as Trash2, Cancel01Icon as X } from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Variant = 'danger' | 'warning' | 'default';

interface ConfirmOpts {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: Variant;
}
interface PromptOpts extends ConfirmOpts {
  defaultValue?: string;
  placeholder?: string;
}

interface DialogsApi {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  prompt: (opts: PromptOpts) => Promise<string | null>;
}

const DialogsContext = createContext<DialogsApi | null>(null);

export function useDialogs(): DialogsApi {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error('useDialogs must be used within <DialogsProvider>');
  return ctx;
}

interface State {
  open: boolean;
  mode: 'confirm' | 'prompt';
  opts: PromptOpts;
  value: string;
}

export function DialogsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ open: false, mode: 'confirm', opts: { title: '' }, value: '' });
  const resolver = useRef<((v: any) => void) | null>(null);

  const close = useCallback((result: boolean | string | null) => {
    setState((s) => ({ ...s, open: false }));
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  const confirm = useCallback((opts: ConfirmOpts) => new Promise<boolean>((resolve) => {
    resolver.current = resolve as (v: any) => void;
    setState({ open: true, mode: 'confirm', opts, value: '' });
  }), []);

  const prompt = useCallback((opts: PromptOpts) => new Promise<string | null>((resolve) => {
    resolver.current = resolve as (v: any) => void;
    setState({ open: true, mode: 'prompt', opts, value: opts.defaultValue || '' });
  }), []);

  const { open, mode, opts, value } = state;
  const variant: Variant = opts.variant || (mode === 'confirm' ? 'default' : 'default');

  const icon = variant === 'danger'
    ? <HugeiconsIcon icon={Trash2} className="h-5 w-5 text-destructive" />
    : variant === 'warning'
    ? <HugeiconsIcon icon={AlertTriangle} className="h-5 w-5 text-yellow-500" />
    : <HugeiconsIcon icon={AlertTriangle} className="h-5 w-5 text-accent" />;

  const confirmBtnClass = variant === 'danger'
    ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
    : variant === 'warning'
    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
    : '';

  return (
    <DialogsContext.Provider value={{ confirm, prompt }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-150" onClick={() => close(mode === 'prompt' ? null : false)} />
          <div
            role="dialog" aria-modal="true"
            className="relative z-10 w-full max-w-md rounded-xl bg-popover ring-1 ring-border shadow-2xl animate-in zoom-in-95 fade-in duration-150"
            onKeyDown={(e) => {
              if (e.key === 'Escape') close(mode === 'prompt' ? null : false);
              if (e.key === 'Enter' && mode === 'prompt') close(value.trim() || null);
            }}
          >
            <div className="flex items-start gap-3 p-5 pb-3">
              <span className="shrink-0 h-9 w-9 rounded-lg bg-muted/60 ring-1 ring-border/60 flex items-center justify-center">{icon}</span>
              <div className="flex-1 min-w-0 pt-0.5">
                <h3 className="text-sm font-semibold text-foreground">{opts.title}</h3>
                {opts.description && <p className="mt-1 text-sm text-muted-foreground">{opts.description}</p>}
              </div>
              <button onClick={() => close(mode === 'prompt' ? null : false)} className="shrink-0 p-1 rounded-md hover:bg-muted/60 text-muted-foreground">
                <HugeiconsIcon icon={X} className="h-4 w-4" />
              </button>
            </div>

            {mode === 'prompt' && (
              <div className="px-5 pb-1">
                <Input
                  autoFocus
                  value={value}
                  placeholder={opts.placeholder}
                  onChange={(e) => setState((s) => ({ ...s, value: e.target.value }))}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 p-5 pt-4">
              <Button variant="outline" size="sm" onClick={() => close(mode === 'prompt' ? null : false)}>
                {opts.cancelText || 'Cancel'}
              </Button>
              <Button
                size="sm"
                className={confirmBtnClass}
                onClick={() => close(mode === 'prompt' ? (value.trim() || null) : true)}
              >
                {opts.confirmText || (mode === 'prompt' ? 'Save' : 'Confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DialogsContext.Provider>
  );
}
